/* ca-cart.js — the reservation layer.
 *
 * Three jobs:
 *   1. Enhance ca-atc forms. They post to /cart/add and land on /cart without
 *      this file; here they go through the Cart AJAX API instead.
 *   2. Keep the floating indicator and every button's reserved count in step
 *      with the cart, on load and after any change.
 *   3. Render and drive the drawer.
 *
 * The drawer is built from /cart.js rather than server-rendered, so one code
 * path formats every state and there is no chance of the initial markup and a
 * later update disagreeing. Money goes through Intl using the currency the
 * cart reports, which is the presentment currency under Shopify Markets — a
 * GB visitor sees pounds without us parsing a money format string.
 *
 * Adding does NOT open the drawer. The button confirms in place and the
 * indicator arrives; interrupting someone mid-page to show them a receipt is
 * the transactional feeling this page is trying not to have.
 */
(function () {
  'use strict';

  var ADD_BUSY = 'Reserving…';
  var LABEL_RESERVED = 'Reserved';
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  var pill, drawer, drawerBody, drawerFoot, drawerTotal;
  var lastFocus = null;
  var cartCache = null;

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'CAD',
        currencyDisplay: 'narrowSymbol'
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---- cart state ------------------------------------------------------ */

  function getCart() {
    return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function apply(cart) {
    cartCache = cart;
    updatePill(cart);
    updateButtons(cart);
    renderDrawer(cart);
  }

  function refresh() {
    return getCart().then(apply).catch(function () { /* cosmetic; never block */ });
  }

  /* ---- indicator ------------------------------------------------------- */

  function updatePill(cart) {
    if (!pill) return;
    var n = cart.item_count;
    var countEl = pill.querySelector('[data-ca-cart-count]');
    if (countEl) countEl.textContent = n;
    pill.setAttribute('aria-label', n + ' item' + (n === 1 ? '' : 's') +
      ' reserved — open your reservation');

    var wasVisible = pill.classList.contains('is-visible');
    if (n > 0) {
      pill.classList.add('is-visible');
      // Animate only when it has just arrived, not on every page load with a
      // cart that was already there from a previous visit.
      if (!wasVisible) {
        pill.classList.remove('is-arriving');
        void pill.offsetWidth;
        pill.classList.add('is-arriving');
      }
    } else {
      pill.classList.remove('is-visible', 'is-arriving');
    }
  }

  /* ---- per-product reserved counts -------------------------------------- */

  function updateButtons(cart) {
    var counts = {};
    (cart.items || []).forEach(function (item) {
      counts[item.handle] = (counts[item.handle] || 0) + item.quantity;
    });

    document.querySelectorAll('[data-ca-atc]').forEach(function (form) {
      var handle = form.getAttribute('data-product-handle');
      var label = form.querySelector('[data-ca-atc-label]');
      var button = form.querySelector('[data-ca-atc-button]');
      if (!label || !button || button.hasAttribute('data-ca-busy')) return;

      var n = counts[handle] || 0;
      var idle = form.getAttribute('data-idle-label') || label.textContent;
      if (!form.getAttribute('data-idle-label')) form.setAttribute('data-idle-label', idle);

      label.textContent = n > 0 ? LABEL_RESERVED + ' (' + n + ')' : idle;
      form.classList.toggle('ca-atc--reserved', n > 0);
    });
  }

  /* ---- drawer ---------------------------------------------------------- */

  function renderDrawer(cart) {
    if (!drawerBody) return;

    if (!cart.item_count) {
      drawerBody.innerHTML = '<p class="ca-drawer__empty">Nothing reserved yet.</p>';
      if (drawerFoot) drawerFoot.hidden = true;
      return;
    }

    var html = '<ul class="ca-drawer__lines">';
    cart.items.forEach(function (item) {
      var img = item.image
        ? '<img src="' + esc(item.image.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2')) +
          '" alt="" width="64" height="80" loading="lazy">'
        : '';
      html +=
        '<li class="ca-drawer__line" data-key="' + esc(item.key) + '">' +
          '<div class="ca-drawer__thumb">' + img + '</div>' +
          '<div class="ca-drawer__detail">' +
            '<p class="ca-drawer__name">' + esc(item.product_title) + '</p>' +
            '<div class="ca-drawer__qty">' +
              '<button type="button" data-ca-qty="-1" aria-label="Reduce quantity">&minus;</button>' +
              '<span class="ca-num" aria-live="polite">' + item.quantity + '</span>' +
              '<button type="button" data-ca-qty="1" aria-label="Increase quantity">+</button>' +
            '</div>' +
          '</div>' +
          '<div class="ca-drawer__linetotal ca-num">' + money(item.line_price, cart.currency) + '</div>' +
        '</li>';
    });
    html += '</ul>';
    drawerBody.innerHTML = html;

    if (drawerFoot) drawerFoot.hidden = false;
    if (drawerTotal) drawerTotal.textContent = money(cart.total_price, cart.currency);
  }

  function openDrawer() {
    if (!drawer) return;
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    document.documentElement.classList.add('ca-drawer-open');
    var close = drawer.querySelector('.ca-drawer__close');
    if (close) close.focus();
    document.addEventListener('keydown', onKeydown);
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    document.documentElement.classList.remove('ca-drawer-open');
    document.removeEventListener('keydown', onKeydown);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function onKeydown(e) {
    if (e.key === 'Escape') { closeDrawer(); return; }
    if (e.key !== 'Tab') return;
    var focusables = drawer.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    var first = focusables[0], last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function changeLine(key, delta) {
    if (!cartCache) return;
    var item = cartCache.items.filter(function (i) { return i.key === key; })[0];
    if (!item) return;
    var qty = Math.max(0, item.quantity + delta);
    fetch(root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    })
      .then(function (r) { return r.json(); })
      .then(apply)
      .catch(function () { window.location.href = root + 'cart'; });
  }

  /* ---- add to cart ------------------------------------------------------ */

  function onSubmit(e) {
    var form = e.target.closest('[data-ca-atc]');
    if (!form) return;

    var button = form.querySelector('[data-ca-atc-button]');
    var label = form.querySelector('[data-ca-atc-label]');
    var error = form.querySelector('[data-ca-atc-error]');
    if (!button || button.disabled) return;

    e.preventDefault();

    button.disabled = true;
    button.setAttribute('data-ca-busy', '');
    if (label) label.textContent = ADD_BUSY;
    if (error) { error.hidden = true; error.textContent = ''; }

    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form)
    })
      .then(function (r) {
        return r.json().then(function (body) {
          if (!r.ok) throw new Error(body.description || body.message || 'Could not reserve this');
          return body;
        });
      })
      .then(function () {
        button.removeAttribute('data-ca-busy');
        button.disabled = false;
        form.dispatchEvent(new CustomEvent('ca:added', { bubbles: true }));
        return refresh();
      })
      .catch(function (err) {
        button.removeAttribute('data-ca-busy');
        button.disabled = false;
        if (error) {
          error.textContent = err.message || 'Could not reserve this. Please try again.';
          error.hidden = false;
          if (cartCache) updateButtons(cartCache);
        } else {
          form.submit();
        }
      });
  }

  /* ---- wiring ---------------------------------------------------------- */

  function init() {
    pill        = document.querySelector('[data-ca-pill]');
    drawer      = document.querySelector('[data-ca-drawer]');
    drawerBody  = drawer && drawer.querySelector('[data-ca-drawer-body]');
    drawerFoot  = drawer && drawer.querySelector('[data-ca-drawer-foot]');
    drawerTotal = drawer && drawer.querySelector('[data-ca-drawer-total]');

    document.addEventListener('submit', onSubmit);

    if (pill) {
      pill.addEventListener('click', function (e) {
        if (!drawer) return;          // no drawer, follow the href to /cart
        e.preventDefault();
        openDrawer();
      });
    }

    if (drawer) {
      drawer.addEventListener('click', function (e) {
        if (e.target.closest('[data-ca-drawer-close]')) { closeDrawer(); return; }
        var qty = e.target.closest('[data-ca-qty]');
        if (qty) {
          var line = qty.closest('[data-key]');
          if (line) changeLine(line.getAttribute('data-key'), parseInt(qty.getAttribute('data-ca-qty'), 10));
        }
      });
    }

    // Seed from the live cart so a returning visitor's counts are right.
    refresh();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
